/**
 * Relay Helper status for the Team page. One poll of GET /api/daemon/status
 * (the same server-mediated source Control Center trusts), owned by TeamPage
 * and passed down so the Relay Helper row, the System row and the setup
 * checklist always agree. The status route carries no host name, so the page
 * shows "Running · last check-in …" without one.
 */
import { useCallback, useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";

export interface HelperStatus {
  online: boolean;
  lastHeartbeat: string | null;
}

export function useHelperStatus(workspaceId: string): HelperStatus {
  const [status, setStatus] = useState<HelperStatus>({ online: false, lastHeartbeat: null });

  const poll = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch("/api/daemon/status", { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus({ online: data.online === true, lastHeartbeat: data.lastHeartbeat ?? null });
    } catch (err) {
      console.warn("[useHelperStatus] status poll", err);
      setStatus((s) => ({ ...s, online: false }));
    }
  }, [workspaceId]);

  useEffect(() => {
    void poll();
    const interval = setInterval(() => void poll(), 15_000);
    return () => clearInterval(interval);
  }, [poll]);

  return status;
}

/** "12s ago", "3m ago", "2h ago", "4d ago". */
export function relativeTime(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
